import _ from 'lodash'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { ChipProps } from '@mui/material/Chip'

import CalendarIcon from '@/components/ui/icons/Calendar'
import DoctorIcon from '@/components/ui/icons/Doctor'
import PatientIcon from '@/components/ui/icons/Patient'

import { ConsultationType } from '@/core/models/consultation.model'
import { ConsultationData } from './ConsultationsTable'
// import { MedicalEquipmentIcon } from '@/assets/svg'

type CardViewProps = {
  data: ConsultationData[]
  isDoctor?: boolean
  onAcceptConsultation: (consultationId: string) => void
  onRefuseConsultation: (consultationId: string) => void
  onOpenConsultationDetails: (consultation: ConsultationData) => void
  onOpenConsultationLive: (consultation: ConsultationData) => void
}

export default function ConsultationsCardView({
  data,
  isDoctor,
  onOpenConsultationDetails,
  onOpenConsultationLive,
  onAcceptConsultation,
  onRefuseConsultation,
}: CardViewProps) {
  const { t } = useTranslation()

  return (
    <Grid container spacing={1}>
      {data.map((consultation) => {
        const { doctor: medecin, patient, showButtons, startDate, status } = consultation

        let color: ChipProps['color']
        let statusText: string = status
        switch (status) {
          case 'scheduled':
          case 'waiting':
            color = 'primary'
            break
          case 'accepted':
            color = 'success'
            break
          case 'canceled':
          case 'refused':
            color = 'error'
            break
          default:
            color = 'info'
            break
        }

        return (
          <Grid item xs={12} sm={6} md={4} key={consultation.id}>
            <Card>
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center">
                        <CalendarIcon fontSize="small" style={{ marginRight: '8px' }} />
                        <Chip
                          color="secondary"
                          label={
                            <span>
                              {dayjs(startDate).isSame(dayjs(), 'day') ? t(`moment:calendar:today`) : dayjs(startDate).format('DD/MM/YYYY')} -{' '}
                              <b>{dayjs(startDate).format('HH[h]mm')}</b>
                            </span>
                          }
                          variant="filled"
                        />
                      </Box>
                      <Chip color={color} label={t(`common:model.consultation.status.${statusText}.label`)} size="small" variant="filled" />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    {/* <Typography variant="subtitle1">{t('page.consultations.table.header.doctor')}</Typography> */}
                    <Box display="flex" alignItems="center">
                      {!!medecin ? (
                        <>
                          <DoctorIcon isFemale={medecin?.gender === 'F'} style={{ marginRight: '8px' }} />
                          <Typography>{_.trim(`${medecin?.name || ''} ${medecin?.surname || ''}`)}</Typography>
                        </>
                      ) : consultation.type === ConsultationType.Local ? (
                        <>
                          {/* <MedicalEquipmentIcon style={{ width: '24px', height: '24px', marginRight: '8px' }} /> */}
                          <Typography sx={{ textTransform: 'capitalize' }}>{t('page.consultations.toSend')}</Typography>
                        </>
                      ) : (
                        <Typography>-</Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    {/* <Typography variant="subtitle1">{t('page.consultations.table.header.patient')}</Typography> */}
                    <Box display="flex" alignItems="center">
                      <PatientIcon isFemale={patient?.gender === 'F'} style={{ marginRight: '8px' }} />
                      <Typography>{_.trim(`${patient?.name || ''} ${patient?.surname || ''}`)}</Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1}>
                      {showButtons?.refuse && (
                        <Button variant="contained" color="error" onClick={() => onRefuseConsultation(consultation.id)}>
                          {t('common:button.refuse.tooltip')}
                        </Button>
                      )}
                      {showButtons?.accept && (
                        <Button color="success" onClick={() => onAcceptConsultation(consultation.id)}>
                          {t('common:button.accept.tooltip')}
                        </Button>
                      )}
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="column" justifyContent="end" spacing={1}>
                      {_.some(['scheduled', 'active', 'accepted'], (status) => status === consultation.status) && (
                        <Button
                          color="info"
                          variant="contained"
                          onClick={() => onOpenConsultationLive(consultation)}
                        >
                          {t('page.consultations.openConsultationLiveButton.button')}
                        </Button>
                      )}
                      <Button variant="contained" onClick={() => onOpenConsultationDetails(consultation)}>
                        {t('page.consultations.openConsultationDetailsButton.button')}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )
      })}
    </Grid>
  )
}
